import { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, FormControlLabel, Radio, Typography } from '@mui/material';

import FButtonDropdown from './FButtonDropdown';
import FHRadioGroup from './FHRadioGroup';
import FHSelect from './FHSelect';
import FHButton from './FHButton';
import FCheckbox from './FCheckbox';
import FHFilterChips from './FHFilterChips';
import { parseIntOrReturnString } from '../../helpers';
import { getThemeColor } from '../../utils/common';
import { FORTA_GRAY3, FORTA_WHITE } from '../../utils/colors';
import { BackgroundTypes, FilterAndSortMode, FilterAndSortType, Themes } from '../../constants';
import FilterIcon from '../svgs/FilterIcon';
import FHAutocomplete from './FHAutocomplete';

import type { ChangeEvent } from 'react';
import type {
  IFilterAndSortItem,
  IFilterChip,
  IFilterOutput,
  IOption,
  SelectValueType,
} from '../../utils/types';

interface IFilter {
  [name: string]: IFilterAndSortItem;
}

interface ILocalFilterItem {
  id: string | number;
  selected: boolean;
}

interface ILocalFilters {
  [name: string]: Array<ILocalFilterItem>;
}

interface IFHFilterAndSortProps {
  buttonLabel?: string;
  colorTheme?: Themes;
  filterSortOptions: IFilter;
  disabled?: boolean;
  // chipWrapperId is the id of dom element in which filterChips
  // are expected to be rendered. Pass chipWrapperId to render filterChips.
  // Filter chips are not applicable for sort mode
  chipWrapperId?: string;
  mode?: FilterAndSortMode;
  showResetButton?: boolean;
  selectedFilter?: IFilterOutput;
  buttonIcon?: (props: any) => JSX.Element;
  onFilterChange: (data: IFilterOutput) => void;
  buttonStyles?: any;
}

interface ICustomSortButtonProps {
  label: string;
  colorTheme: Themes;
  disabled: boolean;
  Icon: (props: any) => JSX.Element;
  buttonStyles?: any;
}

function CustomSortButton({
  label,
  colorTheme,
  disabled,
  Icon,
  buttonStyles,
}: ICustomSortButtonProps) {
  return (
    <FHButton
      data-testid="FilterAndSortButton"
      disabled={disabled}
      sx={{
        backgroundColor: FORTA_WHITE,
        border: `2px solid ${getThemeColor(colorTheme).light2}`,
        borderRadius: '18px',
        '&:hover': {
          backgroundColor: FORTA_WHITE,
          zIndex: 1,
        },
        p: 2,
        ...buttonStyles,
      }}
      colorTheme={colorTheme}
      startIcon={<Icon color={getThemeColor(colorTheme).primary} />}
    >
      <Typography
        color={getThemeColor(colorTheme).primary}
        fontWeight={'bold'}
        textTransform={'none'}
      >
        {label}
      </Typography>
    </FHButton>
  );
}

function LabelRenderer(filterItem: IFilterAndSortItem, colorTheme: Themes) {
  return (
    <>
      {filterItem.label ? (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {filterItem.LabelIcon ? (
            <filterItem.LabelIcon color={getThemeColor(colorTheme).primary} />
          ) : null}
          <Typography fontWeight={600} fontSize={16} ml={filterItem.LabelIcon ? 1 : 0}>
            {filterItem.label}
          </Typography>
        </Box>
      ) : null}
      {filterItem.secondaryLabel ? (
        <Typography color={FORTA_GRAY3} fontSize={14}>
          {filterItem.secondaryLabel}
        </Typography>
      ) : null}
    </>
  );
}

function FHFilterAndSort({
  buttonLabel = 'Filter',
  filterSortOptions = {},
  colorTheme = Themes.Primary,
  disabled = false,
  chipWrapperId = '',
  mode = FilterAndSortMode.Filter,
  showResetButton = false,
  selectedFilter = {},
  buttonIcon = FilterIcon,
  onFilterChange,
  buttonStyles,
}: IFHFilterAndSortProps) {
  const [selectedFiltersAndSort, setSelectedFiltersAndSort] = useState<IFilterOutput>({});
  const [localFilterValues, setLocalFilterValues] = useState<ILocalFilters>({});
  const chipWrapperRef = useRef<HTMLElement>();

  const chipData: IFilterChip[] = useMemo(() => {
    const data: IFilterChip[] = [];
    Object.entries(selectedFiltersAndSort).forEach(([name, items]) => {
      const filterItems = items.map((item) => ({
        id: item.id,
        label: item.label,
        filterName: name,
      }));
      data.push(...filterItems);
    });
    return data;
  }, [selectedFiltersAndSort]);

  useEffect(() => {
    if (!chipWrapperId) return;
    const timeoutId = setTimeout(() => {
      chipWrapperRef.current = document.getElementById(chipWrapperId) ?? undefined;
    }, 0);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [chipWrapperId]);

  useEffect(() => {
    onFilterChange(selectedFiltersAndSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiltersAndSort]);

  useEffect(() => {
    const localFilterInitialState: ILocalFilters = {};
    for (const name in filterSortOptions) {
      localFilterInitialState[name] = filterSortOptions[name].options.map((item) => ({
        id: item.id,
        selected: item.selected ?? false,
      }));
    }
    if (Object.keys(selectedFilter).length > 0) {
      for (const name in selectedFilter) {
        selectedFilter[name].forEach((item) => {
          const itemToUpdate = localFilterInitialState[name]?.find(
            (localFilter) => localFilter.id === item.id,
          );
          if (itemToUpdate) {
            itemToUpdate.selected = true;
          }
        });
      }
    }
    setSelectedFiltersAndSort(selectedFilter);
    setLocalFilterValues(localFilterInitialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSortOptions]);

  const filtersDeepClone: IFilter = useMemo(
    () => JSON.parse(JSON.stringify(filterSortOptions)),
    [filterSortOptions],
  );

  const handleFilterSortSelect = (
    name: string,
    value: ILocalFilterItem,
    type: FilterAndSortType,
    index?: number,
  ) => {
    // Multiple options can be selected at a time, multi-select inputs not applicable in sort mode
    if (
      type === FilterAndSortType.Checkbox &&
      index !== undefined &&
      mode !== FilterAndSortMode.Sort
    ) {
      const newValues = [...(localFilterValues[name] || [])];
      newValues[index] = value;
      setLocalFilterValues({ ...localFilterValues, [name]: newValues });

      const itemToUpdateIndex = selectedFiltersAndSort[name]?.findIndex(
        (item) =>
          parseIntOrReturnString(item.id) === parseIntOrReturnString(value.id),
      );
      if (itemToUpdateIndex > -1) {
        const filterItemArray = [...selectedFiltersAndSort[name]];
        filterItemArray[itemToUpdateIndex].selected = value.selected;
        setSelectedFiltersAndSort({
          ...selectedFiltersAndSort,
          [name]: filterItemArray.filter((option) => option.selected),
        });
      } else {
        const itemToAdd = filtersDeepClone[name]['options'].find(
          (item) =>
            parseIntOrReturnString(item.id) === parseIntOrReturnString(value.id),
        );
        if (itemToAdd) {
          const itemToAddCopy = { ...itemToAdd };
          itemToAddCopy.selected = value.selected;
          setSelectedFiltersAndSort({
            ...selectedFiltersAndSort,
            [name]: [...(selectedFiltersAndSort[name] || []), itemToAddCopy].filter(
              (option) => option.selected,
            ),
          });
        }
      }
    }
    // Single option can be selected at a time, sort is only applicable to single option select input type
    if (
      type === FilterAndSortType.Radio ||
      type === FilterAndSortType.Select ||
      type === FilterAndSortType.AutoComplete
    ) {
      if (mode === FilterAndSortMode.Sort) {
        setLocalFilterValues({ [name]: [value] });
      } else {
        setLocalFilterValues({ ...localFilterValues, [name]: [value] });
      }
      const selectedOption = filtersDeepClone[name].options.find(
        (option) =>
          parseIntOrReturnString(option.id) === parseIntOrReturnString(value.id),
      );
      if (selectedOption) {
        const selectedOptionCopy = { ...selectedOption };
        selectedOptionCopy.selected = true;
        if (mode === FilterAndSortMode.Sort) {
          setSelectedFiltersAndSort({
            [name]: [selectedOptionCopy],
          });
        } else {
          setSelectedFiltersAndSort({
            ...selectedFiltersAndSort,
            [name]: [selectedOptionCopy],
          });
        }
      }
    }
  };

  // Resets filter output and local filter state
  const handleResetFilters = () => {
    const updatedLocalData: ILocalFilters = { ...localFilterValues };
    const updatedFilterData: IFilterOutput = {};
    for (const name in localFilterValues) {
      updatedLocalData[name] = (updatedLocalData[name] || []).map((item) => ({
        ...item,
        selected: false,
      }));
    }
    for (const name in selectedFiltersAndSort) {
      updatedFilterData[name] = [];
    }
    setLocalFilterValues(updatedLocalData);
    setSelectedFiltersAndSort(updatedFilterData);
  };

  // This function updates filter output and local filter state based on chip state
  const handleChipRemove = (data: IFilterChip[]) => {
    const updatedFilterData: IFilterOutput = {};
    const updatedLocalData: ILocalFilters = { ...localFilterValues };

    for (const name in localFilterValues) {
      updatedLocalData[name] = (updatedLocalData[name] || []).map((item) => ({
        ...item,
        selected: false,
      }));
      if (data.some((item) => item.filterName === name)) {
        data.forEach((chip) => {
          if (name === chip.filterName) {
            const filterItem = updatedLocalData[name]?.find(
              (item) =>
                parseIntOrReturnString(item.id) === parseIntOrReturnString(chip.id),
            );
            if (filterItem) {
              filterItem.selected = true;
            }
          }
        });
      }
    }

    for (const name in selectedFiltersAndSort) {
      updatedFilterData[name] = [];
      data.forEach((chip) => {
        if (name === chip.filterName) {
          const filterItem = selectedFiltersAndSort[name].find(
            (item) =>
              parseIntOrReturnString(item.id) === parseIntOrReturnString(chip.id),
          );
          if (filterItem) {
            updatedFilterData[name] = [...updatedFilterData[name], filterItem];
          }
        }
      });
    }
    setLocalFilterValues(updatedLocalData);
    setSelectedFiltersAndSort(updatedFilterData);
  };

  return (
    <Box>
      <FButtonDropdown
        buttonRenderer={() => (
          <CustomSortButton
            label={buttonLabel}
            colorTheme={colorTheme}
            disabled={disabled}
            Icon={buttonIcon}
            buttonStyles={buttonStyles}
          />
        )}
        disabled={disabled}
        preventCloseOnBodyClick
        colorTheme={colorTheme}
      >
        <Box p={2}>
          {showResetButton && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                data-testid="FilterAndSortResetButton"
                fontSize={14}
                fontWeight={700}
                color={getThemeColor(colorTheme).primary}
                textAlign="right"
                onClick={handleResetFilters}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
              >
                reset
              </Typography>
            </Box>
          )}
          {Object.entries(filterSortOptions)
            .sort(
              ([_aName, aFilterItem], [_bName, bFilterItem]) =>
                aFilterItem.order - bFilterItem.order,
            )
            .map(([name, filterItem], i) => {
              return (
                <Box key={name} mt={i === 0 ? 0 : 3}>
                  <Box sx={{ minWidth: 200 }}>
                    {filterItem.type === FilterAndSortType.Radio && (
                      <>
                        {LabelRenderer(filterItem, colorTheme)}
                        <FHRadioGroup
                          data-testid={filterItem.dataTestid ? filterItem.dataTestid : null}
                          rowGap={'10px'}
                          colorTheme={colorTheme}
                          coloredBackground
                          value={
                            localFilterValues[name]?.[0]?.['selected']
                              ? localFilterValues[name]?.[0]?.['id']
                              : ''
                          }
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            handleFilterSortSelect(
                              name,
                              {
                                id: parseIntOrReturnString(e.target.value),
                                selected: true,
                              },
                              filterItem.type,
                            );
                          }}
                        >
                          {filterItem.options.map((item) => {
                            return (
                              <FormControlLabel
                                key={item.id}
                                disabled={item.disabled ?? false}
                                sx={{ width: 'fit-content' }}
                                value={item.id}
                                control={<Radio disableRipple />}
                                label={item.label}
                                data-testid={item.dataTestId ? item.dataTestId : null}
                              />
                            );
                          })}
                        </FHRadioGroup>
                      </>
                    )}
                    {filterItem.type === FilterAndSortType.Select && (
                      <>
                        {LabelRenderer(filterItem, colorTheme)}
                        <FHSelect
                          data-testid={filterItem.dataTestid ? filterItem.dataTestid : null}
                          sx={{ width: '100%', boxShadow: 'none' }}
                          menuProps={{
                            maxHeight: '500px',
                            '& .MuiPaper-elevation': {
                              boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
                            },
                          }}
                          inputHeight={40}
                          backgroundType={BackgroundTypes.Colored}
                          colorTheme={colorTheme}
                          disabled={filterItem.disabled ?? false}
                          options={filterItem.options.map((item) => ({
                            value: item.id,
                            label: item.label,
                          }))}
                          selectFirstOption={false}
                          optionDataTestIdPrefix="filterItemOption"
                          value={
                            localFilterValues[name]?.[0]?.['selected']
                              ? localFilterValues[name]?.[0]?.['id']
                              : ''
                          }
                          onUpdate={(value: SelectValueType) => {
                            handleFilterSortSelect(
                              name,
                              {
                                id: parseIntOrReturnString(value),
                                selected: true,
                              },
                              filterItem.type,
                            );
                          }}
                        />
                      </>
                    )}
                    {filterItem.type === FilterAndSortType.Checkbox &&
                      mode === FilterAndSortMode.Filter && (
                        <>
                          {LabelRenderer(filterItem, colorTheme)}
                          <Box
                            sx={{ display: 'flex', flexDirection: 'column' }}
                            data-testid={filterItem.dataTestid ? filterItem.dataTestid : null}
                          >
                            {filterItem.options.map((item, itemIndex) => (
                              <FCheckbox
                                key={itemIndex}
                                data-testid={item.dataTestId ? item.dataTestId : null}
                                label={item.label}
                                colorTheme={colorTheme}
                                coloredBackground
                                sx={{
                                  mb: 1,
                                  flexGrow: 1,
                                  justifyContent: 'center',
                                }}
                                disabled={item.disabled ?? false}
                                checked={
                                  Boolean(item.selected) ||
                                  Boolean(localFilterValues[name]?.[itemIndex]?.['selected'])
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                  handleFilterSortSelect(
                                    name,
                                    {
                                      id: parseIntOrReturnString(item.id),
                                      selected: e.target.checked,
                                    },
                                    filterItem.type,
                                    itemIndex,
                                  );
                                }}
                              />
                            ))}
                          </Box>
                        </>
                      )}
                    {filterItem.type === FilterAndSortType.AutoComplete && (
                      <>
                        {LabelRenderer(filterItem, colorTheme)}
                        <FHAutocomplete
                          data-testid={filterItem.dataTestid ? filterItem.dataTestid : null}
                          sx={{ width: 'auto', boxShadow: 'none' }}
                          menuProps={{
                            maxHeight: '500px',
                            '& .MuiPaper-elevation': {
                              boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
                            },
                          }}
                          inputHeight={40}
                          colorTheme={colorTheme}
                          coloredBackground
                          disableClearable
                          disabled={filterItem.disabled ?? false}
                          options={filterItem.options.map((item) => ({
                            id: item.id,
                            label: item.label,
                          }))}
                          selectFirstOption={false}
                          optionDataTestIdPrefix="filterItemOption"
                          value={
                            localFilterValues[name]?.[0]?.['selected']
                              ? filterItem.options.find(
                                  (item) =>
                                    String(item.id) ===
                                    String(localFilterValues[name]?.[0]?.['id']),
                                )
                              : ''
                          }
                          onChange={(
                            _: ChangeEvent<HTMLSelectElement>,
                            newValue: IOption | null,
                          ) => {
                            if (!newValue?.id) return;
                            handleFilterSortSelect(
                              name,
                              {
                                id: parseIntOrReturnString(newValue.id),
                                selected: true,
                              },
                              filterItem.type,
                            );
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      </FButtonDropdown>
      {chipWrapperRef.current &&
        createPortal(
          <FHFilterChips chipData={chipData} onChipRemove={handleChipRemove} />,
          chipWrapperRef.current,
        )}
    </Box>
  );
}

export default FHFilterAndSort;
